import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import CartIcon from './CartIcon'
import UserMenu from './UserMenu'
import { MAIN_NAV, CONTACT_INFO, SITE_LINKS } from '../constants/siteLinks'

/**
 * Shared site header — same links on every page.
 * @param {{ cartCount?: number, onCartClick?: () => void }} props
 */
function SiteHeader({ cartCount = 0, onCartClick }) {
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/'
    if (href.startsWith('/#')) return location.pathname === '/' && location.hash === href.slice(1)
    return location.pathname === href || location.pathname.startsWith(`${href}/`)
  }

  const openMenu = () => setMenuOpen(true)
  const closeMenu = () => setMenuOpen(false)

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname, location.hash])

  useEffect(() => {
    if (!menuOpen) return undefined
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previous
    }
  }, [menuOpen])

  return (
    <>
      <div className="search__popup">
        <div className="container">
          <div className="row">
            <div className="col-xxl-12">
              <div className="search__wrapper">
                <div className="search__top d-flex justify-content-between align-items-center">
                  <div className="search__logo">
                    <Link to={SITE_LINKS.home}>
                      <img src="/assets/img/logo/F_logo.png" alt="Fabritech" style={{ height: 40, width: 'auto' }} />
                    </Link>
                  </div>
                  <div className="search__close">
                    <button type="button" className="search__close-btn search-close-btn" aria-label="Close search">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17 1L1 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M1 1L17 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="search__form">
                  <form action="/shop" method="get">
                    <div className="search__input">
                      <input className="search-input-field" type="text" name="q" placeholder="Type here to search..." />
                      <span className="search-focus-border"></span>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tv-offcanvas-area">
        <aside className={`itoffcanvas fab-offcanvas${menuOpen ? ' opened' : ''}`} aria-hidden={!menuOpen}>
          <div className="fab-offcanvas__top">
            <Link to={SITE_LINKS.home} className="fab-offcanvas__brand" onClick={closeMenu}>
              <img src="/assets/img/logo/F_logo.png" alt="Fabritech" />
            </Link>
            <button type="button" className="fab-offcanvas__close close-btn" aria-label="Close menu" onClick={closeMenu}>
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </div>

          <div className="fab-offcanvas__body">
            <p className="fab-offcanvas__kicker">Menu</p>
            <nav className="tv-menu-mobile" aria-label="Mobile">
              <ul className="fab-offcanvas-nav">
                {MAIN_NAV.map((item) => (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className={isActive(item.href) ? 'is-active' : undefined}
                      onClick={closeMenu}
                    >
                      <span>{item.label}</span>
                      <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          <div className="fab-offcanvas__foot">
            <p className="fab-offcanvas__kicker">Contact</p>
            <ul className="fab-offcanvas-contact">
              <li>
                <a href={CONTACT_INFO.emailHref}>
                  <span className="fab-offcanvas-contact__icon" aria-hidden="true">
                    <i className="fa-solid fa-envelope" />
                  </span>
                  <span className="fab-offcanvas-contact__text">
                    <span className="fab-offcanvas-contact__label">Email</span>
                    <span className="fab-offcanvas-contact__value">{CONTACT_INFO.email}</span>
                  </span>
                </a>
              </li>
              <li>
                <a href={CONTACT_INFO.phoneHref}>
                  <span className="fab-offcanvas-contact__icon" aria-hidden="true">
                    <i className="fa-solid fa-phone" />
                  </span>
                  <span className="fab-offcanvas-contact__text">
                    <span className="fab-offcanvas-contact__label">Phone</span>
                    <span className="fab-offcanvas-contact__value">{CONTACT_INFO.phone}</span>
                  </span>
                </a>
              </li>
              <li>
                <a href={CONTACT_INFO.mapsHref} target="_blank" rel="noreferrer">
                  <span className="fab-offcanvas-contact__icon" aria-hidden="true">
                    <i className="fa-solid fa-location-dot" />
                  </span>
                  <span className="fab-offcanvas-contact__text">
                    <span className="fab-offcanvas-contact__label">Location</span>
                    <span className="fab-offcanvas-contact__value">{CONTACT_INFO.address}</span>
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </aside>
      </div>
      <div
        className={`body-overlay${menuOpen ? ' apply' : ''}`}
        onClick={closeMenu}
        aria-hidden={!menuOpen}
      />

      <header className="tv-header-height">
        <div className="tv-header-top-area tv-header-top-ptb d-none d-md-block">
          <div className="container container-1750">
            <div className="row align-items-center">
              <div className="col-xl-8 col-lg-7 col-md-6 col-sm-6">
                <div className="tv-header-top-list-box">
                  <ul>
                    <li className="d-none d-lg-inline-block">
                      <span>
                        <i className="fa-solid fa-phone"></i>
                        <a href={CONTACT_INFO.phoneHref}> {CONTACT_INFO.phone}</a>
                      </span>
                    </li>
                    <li>
                      <span>
                        <i className="fa-solid fa-envelope"></i>
                        <a href={CONTACT_INFO.emailHref}>{CONTACT_INFO.email}</a>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="col-xl-4 col-lg-5 col-md-6 col-sm-6 d-none d-sm-block">
                <div className="tv-header-top-right d-flex align-items-center justify-content-end">
                  <div className="tv-header-top-social-box">
                    <a href="https://twitter.com" target="_blank" rel="noreferrer" aria-label="Twitter"><i className="fa-brands fa-twitter"></i></a>
                    <a href="https://linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn"><i className="fa-brands fa-linkedin-in"></i></a>
                    <a href="https://www.instagram.com/fabritech_ltd/" target="_blank" rel="noreferrer" aria-label="Instagram"><i className="fa-brands fa-instagram"></i></a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="header-sticky" className="tv-header-area header-style-1 tv-header-ptb p-relative">
          <style>{`
            #header-sticky.header-sticky .row > div:first-child,
            #header-sticky.header-sticky [class*="col-"]:first-child {
              background: transparent !important;
            }
            #header-sticky.header-sticky .tv-header-logo,
            #header-sticky.header-sticky .tv-header-logo * {
              background: transparent !important;
            }
          `}</style>
          <div className="container container-1750">
            <div className="p-relative">
              <div className="row align-items-center">
                <div className="col-xxl-2 col-xl-2 col-6">
                  <div className="tv-header-logo">
                    <Link to={SITE_LINKS.home}>
                      <img src="/assets/img/logo/F_logo.png" alt="Fabritech" style={{ height: 45, width: 'auto' }} />
                    </Link>
                  </div>
                </div>
                <div className="col-xxl-7 col-xl-7 d-none d-xl-block">
                  <div className="tv-header-menu tv-header-dropdown">
                    <nav className="tv-menu-content">
                      <ul>
                        {MAIN_NAV.map((item) => (
                          <li key={item.href} className={isActive(item.href) ? 'active' : undefined}>
                            <Link to={item.href}>{item.label}</Link>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  </div>
                </div>
                <div className="col-xxl-3 col-xl-3 col-6">
                  <div className="tv-header-right-action d-flex justify-content-end align-items-center" style={{ gap: 12 }}>
                    <UserMenu />
                    <CartIcon cartCount={cartCount} onClick={onCartClick} />
                    <div className="tv-header-bar d-xl-none">
                      <button
                        type="button"
                        className="tv-menu-bar fab-header-menu"
                        aria-label="Open menu"
                        aria-expanded={menuOpen}
                        onClick={openMenu}
                      >
                        <i className="fa-solid fa-bars" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  )
}

export default SiteHeader
